import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { checkNonMoneyDataForTable, trimObject } from "utils/utils";
import { deleteRole, getRoleList } from "./model";
import s from "./roleManagement.module.scss";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import ButtonMAS from "ButtonMAS/ButtonMAS";

import rolesAndPermissions from "routes/rolesAndPermissions";
import responseCodeMapping from "utils/response_code_mapping.json";
import useRefresh from "utils/useRefresh/useRefresh";

const PAGE_LOCALE_KEY = "page.rolePermissions.roleList.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

const roleManagement = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const isInitRef = useRef(true);

  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const DELETE_DIALOG_INIT_STATE = { id: "", name: "" };
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteDialogData, setDeleteDialogData] = useState(
    DELETE_DIALOG_INIT_STATE,
  );
  const [submitting, setSubmitting] = useState(false);

  useRefresh({
    pagePath: path,
    preventRefresh: loading,
    redirectFn,
    effectFn: () => handleSearch(),
  });

  useEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    initialFetchList(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, []);

  const initialFetchList = async (getIsOudated) => {
    setLoading(true);
    const params = trimObject({});
    const payload = {
      page: 0,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await getRoleList(payload);
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            isInitRef.current = false;
            setDatasource({
              ...datasource,
              data: responseBody.data.roleList,
              total: responseBody?.total,
              page: responseBody?.page + 1,
            });
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }

    setSearch(false);
    setLoading(false);
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    };
    const apiResponse = await getRoleList(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            isInitRef.current = false;
            setDatasource({
              ...datasource,
              data: responseBody.data.roleList,
              total: responseBody?.total,
              page: responseBody?.page + 1,
            });
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }
    setSearch(true);
    setSearching(false);
  };

  // ===================== delete dialog function ===================== //

  const openDeleteDialog = (data) => {
    setDeleteDialogData({ ...deleteDialogData, id: data.id, name: data.name });
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogData(DELETE_DIALOG_INIT_STATE);
    setIsDeleteDialogOpen(false);
  };

  const deleteResultCallback = async () => {
    if (deleteDialogData.id == "") {
      return;
    }
    setSubmitting(true);

    const apiResponse = await deleteRole(deleteDialogData.id);

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            snackbar(
              t(DIALOG_LOCALE_KEY + "deleteRole.msgFailed") +
                responseCodeMapping[responseBody.responseCode ?? "error"]["en"],
              "error",
            );
          } else {
            snackbar(t(DIALOG_LOCALE_KEY + "deleteRole.msgSuccess"), "success");
          }
        }
      }
    }

    setSubmitting(false);
    closeDeleteDialog();
    handleSearch();
  };

  const deleteDialogContent = () => {
    return (
      <span>
        {t(DIALOG_LOCALE_KEY + "deleteRole.confirmMsg") + deleteDialogData.name}
      </span>
    );
  };

  // ===================== delete dialog function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const columns = [
    {
      key: "id",
      title: t(PAGE_LOCALE_KEY + "columnTitle.roleId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },

    {
      key: "name",
      title: t(PAGE_LOCALE_KEY + "columnTitle.roleName"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },

    {
      key: "description",
      title: t(PAGE_LOCALE_KEY + "columnTitle.roleDescription"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "action",
      title: t(PAGE_LOCALE_KEY + "columnTitle.action"),
      sorter: false,
      render: (data) => {
        return (
          <div className={s.actionBtnContainer}>
            <ButtonMAS
              variant="text-only"
              className={s.actionBtn}
              onClick={() => {
                const path = "/roles-and-permissions/roleCreate/roleCreate";
                const payload = {
                  name:
                    rolesAndPermissions.find((tab) => tab.path === path)
                      .tabName + data.id,
                  path: path,
                  state: {
                    id: data.id,
                    createFlag: false,
                  },
                };
                redirectFn("redirectPage", payload);
              }}
            >
              {t(PAGE_LOCALE_KEY + "columnTitle.actions.edit")}
            </ButtonMAS>

            <ButtonMAS
              variant="text-only"
              className={s.actionBtn}
              onClick={() => {
                console.log("delete btn");
                openDeleteDialog({
                  id: data.id,
                  name: data.name,
                });
              }}
            >
              {t(PAGE_LOCALE_KEY + "columnTitle.actions.delete")}
            </ButtonMAS>
          </div>
        );
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <FunctionalButtonBar
          loading={loading}
          refreshBtn
          handleRefresh={handleSearch}
          createBtn
          handleCreate={() => {
            const path = "/roles-and-permissions/roleCreate/roleCreate";
            const payload = {
              name: "Create Role",
              path: path,
            };
            redirectFn("redirectPage", payload);
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
          {submitting && <Spin type={"search"} />}

          <SimpleDialog
            open={isDeleteDialogOpen}
            dialogOncloseFn={closeDeleteDialog}
            title={t(DIALOG_LOCALE_KEY + "deleteRole.title")}
            content={deleteDialogContent()}
            positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
            negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
            confirmFn={!submitting ? deleteResultCallback : () => {}}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(roleManagement);
